import React from "react";
import PageFrame from "./PageFrame";
import PageHeading from "./PageHeading";
import Abs from "./Abs";
import {DASH} from "../bov-stats";

/**
 * Page 05 - Capital Flows, Buyer Origin Then & Now.
 *
 * Layout, all taken from the template:
 *   header rule      y=112,  x 48..1008
 *   dark panel       x 47..521,  y 130..584   (US map artwork + heading)
 *   pie card         x 547..1006, y 130..394
 *   rule             y=410,  x 547..1010, 2px
 *   takeaway         y=421
 *   rule             y=618,  x 48..1008      (top of the figures band)
 *   figure dividers  x=259, 565, 777
 *   rule             y=737,  x 48..1008, 3px (foot of the figures band)
 *
 * The two origin charts and the "2.3x" growth figure are template content, not
 * statistics - they describe the market shift the page is arguing for, and the template
 * prints the same values on every deck. The four figures along the foot ARE per-deck
 * statistics and print a dash until their services exist.
 */

// Template content. TX is the bright green, California black, everything else pale.
const BUYER_ORIGIN = {
    historic: {year: '2007', tx: '29%', ca: '60%', other: '11%'},
    current: {tx: '67%', ca: '18%', other: '15%'},
};

/**
 * The year on the current-origin panel and the right-hand donut: the last complete year, so
 * 2025 on a deck produced in 2026. The template hardcodes 2025 because that is when it was
 * drawn; deriving it means the page does not silently go stale every January.
 *
 * Only that label moves. 2007 is the fixed comparison year the whole page argues from - it
 * appears in the left donut and in the takeaway sentence - and the percentages either side are
 * template content rather than statistics, so they stay as they are until someone reworks the
 * research behind them.
 */
function lastCompleteYear() {
    return String(new Date().getFullYear() - 1);
}

const TEXAS_GROWTH = '2.3x';

const SEGMENT_FILLS = {tx: '#9cd56e', ca: '#221f1f', other: '#c9deae'};

/**
 * The first two figures are the deck's own tenancy, the last two are all closings.
 *
 * Both reference templates print MULTI-TENANT in those first two cells, single-tenant decks
 * included, and take the figures from the multi-tenant columns. That is a copy-paste in the
 * source design, not intent: a single-tenant BOV quoting multi-tenant closings says nothing
 * about the deal in front of the reader. So a single-tenant deck prints its own pair, and this
 * is a deliberate departure from the reference.
 */
const TENANCY_FIGURES = {
    multi: {
        valueKey: 'multiTenantValueSold',
        countKey: 'multiTenantPropertiesSold',
        valueLabel: ['TOTAL VALUE OF', 'MULTI-TENANT SOLD'],
        countLabel: ['TOTAL NUMBER OF ALL', 'MULTI-TENANT PROPERTIES SOLD'],
    },
    single: {
        valueKey: 'singleTenantValueSold',
        countKey: 'singleTenantPropertiesSold',
        valueLabel: ['TOTAL VALUE OF', 'SINGLE-TENANT SOLD'],
        countLabel: ['TOTAL NUMBER OF ALL', 'SINGLE-TENANT PROPERTIES SOLD'],
    },
    // Keyed by layout rather than tenancy: a Multi-Family deck is multi-tenant by routing, but
    // the pair that says something about the deal is its own asset class's.
    'multi-family': {
        valueKey: 'multiFamilyValueSold',
        countKey: 'multiFamilyPropertiesSold',
        valueLabel: ['TOTAL VALUE OF', 'MULTI-FAMILY SOLD'],
        countLabel: ['TOTAL NUMBER OF ALL', 'MULTI-FAMILY PROPERTIES SOLD'],
    },
};

// Figure cells, bounded by the dividers at x=259, 565 and 777.
function figureCells(template) {
    const {layout, tenancy} = template || {};
    const own = TENANCY_FIGURES[layout] || TENANCY_FIGURES[tenancy] || TENANCY_FIGURES.multi;
    return [
        {x: 48,  w: 211, key: own.valueKey,          label: own.valueLabel},
        {x: 259, w: 306, key: own.countKey,          label: own.countLabel},
        {x: 565, w: 212, key: 'totalValueSold',      label: ['TOTAL VALUE SOLD']},
        {x: 777, w: 231, key: 'totalPropertiesSold', label: ['TOTAL NUMBER OF ALL', 'PROPERTIES SOLD']},
    ];
}

export default function CapitalFlows({template, stats, subProduct, pageNo, totalPages}) {
    // A statistic that has been served wins; otherwise the template's own value stands.
    const growth = stats.texasBuyerGrowth !== DASH ? stats.texasBuyerGrowth : TEXAS_GROWTH;
    const cells = figureCells(template);
    const currentYear = lastCompleteYear();

    return (
        <PageFrame fixed pageNo={pageNo} totalPages={totalPages}>
            <PageHeading
                eyebrow={`CAPITAL FLOWS  •  ${subProduct}`}
                title='Where capital is '
                titleAccent='flowing.'
            />

            <Abs x={48} y={112} w={960} className='bov-rule'/>

            {/* Dark panel, 474 x 454 */}
            <Abs x={47} y={130} w={474} h={454} className='bov-capital__panel'>
                <Abs x={17} y={15} w={430}>
                    <div className='bov-capital__panelKicker'>{`HISTORIC BUYER ORIGIN  •  ${currentYear}`}</div>
                    <div className='bov-capital__panelHeadline'>
                        Most Texas Properties are<br/>Purchased by Texans.
                    </div>
                </Abs>
                {template.capitalMap ? (
                    <img className='bov-capital__panelMap' src={template.capitalMap} alt=''/>
                ) : null}
            </Abs>

            {/* Pie card, 459 x 264 */}
            <Abs x={547} y={130} w={459} h={264} className='bov-capital__card'>
                <Abs x={21} y={16} w={420}>
                    <div className='bov-capital__cardKicker'>BUYER ORIGIN  •  THEN &amp; NOW</div>
                    <div className='bov-capital__cardHeadline'>The shift in where investors come from.</div>
                </Abs>

                <OriginChart centreX={112} origin={BUYER_ORIGIN.historic}/>
                <OriginChart centreX={346} origin={{...BUYER_ORIGIN.current, year: currentYear}}/>
            </Abs>

            <Abs x={547} y={410} w={463} h={2} className='bov-rule'/>

            <Abs x={545} y={421} w={465}>
                <div className='bov-capital__takeawayTitle'>THE TAKEAWAY</div>
                <div className='bov-capital__takeaway' style={{marginTop: '14px'}}>
                    Texas-origin buyers have grown <em>{growth}</em> since 2007 - now driving two
                    thirds of multi tenant retail transaction volume. Capital that once flowed
                    primarily from California is now sourced locally, with steady inflows from
                    out-of-state investors filling the rest.
                </div>
            </Abs>

            {/* Figures band */}
            <Abs x={48} y={618} w={960} className='bov-rule'/>
            {[259, 565, 777].map(x => (
                <Abs key={x} x={x} y={619} w={1} h={118} className='bov-rule'/>
            ))}

            {cells.map(cell => (
                <Abs key={cell.key} x={cell.x + 16} y={636} w={cell.w - 24}>
                    <div className='bov-capital__figure'>{splitSuffix(stats[cell.key])}</div>
                    <div className='bov-capital__figureLabel' style={{marginTop: '4px'}}>
                        {cell.label.map((line, i) => <div key={i}>{line}</div>)}
                    </div>
                </Abs>
            ))}

            <Abs x={48} y={737} w={960} h={3} className='bov-rule'/>
        </PageFrame>
    );
}

/**
 * A donut of TX / CA / Other with the Texas share called out in the hole, drawn to the
 * template's proportions.
 */
function OriginChart({centreX, origin}) {
    const segments = [
        {key: 'tx', label: 'TX', value: origin.tx},
        {key: 'ca', label: 'CA', value: origin.ca},
        {key: 'other', label: 'OTHER', value: origin.other},
    ];

    const shares = segments.map(s => parseFloat(s.value) || 0);
    const total = shares.reduce((sum, share) => sum + share, 0) || 100;

    let cursor = -90;
    const wedges = shares.map((share, i) => {
        const from = cursor;
        cursor += (share / total) * 360;
        return {d: wedgePath(from, cursor), fill: SEGMENT_FILLS[segments[i].key]};
    });

    return (
        <Abs x={centreX - 70} y={82} w={140}>
            <div className='bov-capital__pieYear'>{origin.year}</div>

            <div className='bov-capital__pieWrap'>
                <svg className='bov-capital__pieSvg' viewBox='0 0 100 100' role='presentation'>
                    {wedges.map((wedge, i) => <path key={i} d={wedge.d} fill={wedge.fill}/>)}
                    {/* the hole */}
                    <circle cx='50' cy='50' r='27' fill='#ffffff'/>
                </svg>
                <div className='bov-capital__pieCentre'>
                    <div>{origin.tx}</div>
                    <div>{origin.label || 'TX'}</div>
                </div>
            </div>

            <div className='bov-capital__legend'>
                {segments.map(segment => (
                    <span key={segment.key} className='bov-capital__legendItem'>
                        <i className='bov-capital__swatch' style={{background: SEGMENT_FILLS[segment.key]}}/>
                        {segment.label} {segment.value}
                    </span>
                ))}
            </div>
        </Abs>
    );
}

function wedgePath(fromDeg, toDeg) {
    const r = 50;
    const point = deg => {
        const rad = (deg * Math.PI) / 180;
        return [50 + r * Math.cos(rad), 50 + r * Math.sin(rad)];
    };
    const [x0, y0] = point(fromDeg);
    const [x1, y1] = point(toDeg);
    const largeArc = toDeg - fromDeg > 180 ? 1 : 0;
    return `M 50 50 L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
}

/**
 * The template sets the unit suffix of a figure in green ("$3.3" + "B+"). Only attempted
 * on real values - a dash is left alone.
 */
function splitSuffix(value) {
    const match = /^([^A-Za-z+]*)([A-Za-z+]+)$/.exec(String(value));
    if (!match) return value;
    return <span>{match[1]}<em>{match[2]}</em></span>;
}
